/**
 * Org-safe facade over the generated feature-flags client.
 *
 * Every hook requires a branded {@link OrgId}, so managing flags without an
 * active org is a compile error (see `./org-id.ts`). Delegates to the
 * orval-generated hooks in `../generated/flags`. Caches are partitioned by
 * org so flag state never bleeds across a mid-session org switch.
 */
import { useQueryClient } from '@tanstack/react-query';
import {
  useDeleteFeatureFlagOverride,
  useGetFeatureFlagOverrides,
  useListFeatureFlags,
  useSetFeatureFlag,
  useSetFeatureFlagOverride,
} from '../generated/flags';
import type { OrgId } from './org-id';

const flagsRootKey = ['flags'] as const;
const flagsKey = (org: OrgId) => [...flagsRootKey, org] as const;
const accountFlagsKey = [...flagsRootKey, 'account'] as const;
const overridesKey = (org: OrgId, key: string) => ['flag-overrides', org, key] as const;

/** List the org's feature flags. `org` is required at compile time. */
export function useFeatureFlags(org: OrgId) {
  return useListFeatureFlags({ query: { queryKey: flagsKey(org) } });
}

/** List a flag's per-user overrides. `org` is required at compile time. */
export function useFeatureFlagOverrides(org: OrgId, key: string) {
  return useGetFeatureFlagOverrides(key, {
    query: { queryKey: overridesKey(org, key), enabled: Boolean(key) },
  });
}

/** Toggle a global flag, invalidating both org and session-scoped flag lists on success. */
export function useSetFeatureFlagMutation(org: OrgId) {
  const qc = useQueryClient();
  return useSetFeatureFlag({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          qc.invalidateQueries({ queryKey: flagsKey(org) }),
          qc.invalidateQueries({ queryKey: accountFlagsKey }),
        ]);
      },
    },
  });
}

/** Set a per-user flag override, invalidating that flag's overrides on success. */
export function useSetFeatureFlagOverrideMutation(org: OrgId) {
  const qc = useQueryClient();
  return useSetFeatureFlagOverride({
    mutation: {
      onSuccess: (_data, variables) =>
        void qc.invalidateQueries({ queryKey: overridesKey(org, variables.key) }),
    },
  });
}

/** Clear a per-user flag override, invalidating that flag's overrides on success. */
export function useDeleteFeatureFlagOverrideMutation(org: OrgId) {
  const qc = useQueryClient();
  return useDeleteFeatureFlagOverride({
    mutation: {
      onSuccess: (_data, variables) =>
        void qc.invalidateQueries({ queryKey: overridesKey(org, variables.key) }),
    },
  });
}
